import {
  collection,
  addDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { createFundInvestorCapitalTranche } from './fundInvestorCapitalTranchesService';

const COLLECTION = 'fundInvestorRepayments';

/**
 * Add a capital repayment for a fund investor.
 * @param {string} fundInvestorId
 * @param {{ lenderUid: string, investorUserId?: string | null, amount: number, repaidAt: Date | string, repaymentType: 'partial' | 'full', note?: string, investingEntityType?: 'individual' | 'company', investingCompanyId?: string | null, investingDisplayName?: string | null }} data
 * @returns {Promise<string>} repayment doc id
 */
export async function createFundInvestorRepayment(fundInvestorId, data) {
  const {
    lenderUid,
    investorUserId,
    amount,
    repaidAt,
    repaymentType,
    note,
    investingEntityType,
    investingCompanyId,
    investingDisplayName,
  } = data;
  if (!fundInvestorId || !lenderUid || amount == null || amount < 0) throw new Error('fundInvestorId, lenderUid and amount required');
  const at = repaidAt instanceof Date ? repaidAt : (typeof repaidAt === 'string' ? new Date(repaidAt) : new Date());
  const type = repaymentType === 'full' ? 'full' : 'partial';
  const entityType = investingEntityType === 'company' ? 'company' : 'individual';
  const docRef = await addDoc(collection(firestore, COLLECTION), {
    fundInvestorId,
    lenderUid,
    investorUserId: investorUserId || null,
    amount: Number(amount),
    repaidAt: at,
    repaymentType: type,
    note: note ? String(note).trim() : null,
    investingEntityType: entityType,
    investingCompanyId: investingCompanyId && String(investingCompanyId).trim() ? String(investingCompanyId).trim() : null,
    investingDisplayName: investingDisplayName && String(investingDisplayName).trim() ? String(investingDisplayName).trim() : null,
    createdAt: serverTimestamp(),
  });
  const repaidDay =
    at instanceof Date
      ? at.toISOString().slice(0, 10)
      : typeof repaidAt === 'string'
        ? String(repaidAt).trim().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
  try {
    await createFundInvestorCapitalTranche({
      fundInvestorId,
      lenderUid,
      amount: -Math.abs(Number(amount)),
      startDate: repaidDay,
      type: 'repayment',
      sourceRepaymentId: docRef.id,
      note: note ? String(note).trim().slice(0, 500) : null,
    });
  } catch (e) {
    console.error('[fundInvestorRepayments] createFundInvestorCapitalTranche', e);
  }
  return docRef.id;
}

/**
 * List repayments for a fund investor (by repaidAt desc).
 * When called by the investor, pass { investorUserId } so the query is allowed by Firestore rules.
 * @param {string} fundInvestorId
 * @param {{ investorUserId?: string, lenderUid?: string }} [options] - pass investorUserId when caller is investor, lenderUid when caller is lender
 * @returns {Promise<Array<{ id: string, amount: number, repaidAt: object, repaymentType: string, note?: string }>>}
 */
export async function getFundInvestorRepayments(fundInvestorId, options = {}) {
  if (!fundInvestorId) return [];
  const constraints = [
    where('fundInvestorId', '==', fundInvestorId),
    orderBy('repaidAt', 'desc'),
  ];
  if (options.investorUserId) {
    constraints.splice(1, 0, where('investorUserId', '==', options.investorUserId));
  } else if (options.lenderUid) {
    constraints.splice(1, 0, where('lenderUid', '==', options.lenderUid));
  }
  const q = query(collection(firestore, COLLECTION), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      ...data,
      repaidAt: data.repaidAt?.toDate?.() || data.repaidAt,
    };
  });
}

/**
 * Subscribe to repayments for a fund investor (caller must be the lender).
 * Requires lenderUid so the query only returns documents the current user can read (Firestore rules).
 * @param {string} fundInvestorId
 * @param {string} lenderUid - current user (lender) uid
 * @param {(items: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeFundInvestorRepayments(fundInvestorId, lenderUid, callback) {
  if (!fundInvestorId || !lenderUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('fundInvestorId', '==', fundInvestorId),
    where('lenderUid', '==', lenderUid),
    orderBy('repaidAt', 'desc')
  );
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          repaidAt: data.repaidAt?.toDate?.() || data.repaidAt,
        };
      });
      callback(items);
    },
    (err) => {
      console.error('[fundInvestorRepayments] snapshot error:', err?.code, err?.message);
      callback([]);
    }
  );
}
